import { maskieren, neueKennung } from '@netcon-work/notizbuch-kern'
import { useCallback, useMemo } from 'react'
import type { TerminKurz } from './blatt'
import { useMeine, useSpeichern, useStand, type SpeichernEingabe } from './daten'
import { titelAusText } from './hilfen'
import { einreihen } from './warteschlange'

/**
 * **Eine Notiz aus „My Work" ablegen** ([Z-24](../../../../docs/phasen/zusatz-24-my-work.md) AP 24.12).
 *
 * Der Weg ist derselbe wie beim „Schnell notieren" im Heute des Notizbuchs (`erfassen.tsx`):
 * `notiz.speichern`, ohne Netz in die Warteschlange des Geräts. Neu sind nur die beiden Ziele, die
 * My Work kennt:
 *
 * - **Termin** – die Notiz bekommt die Abschrift des Termins (mit Teilnehmern und Serie), genau wie
 *   ein Blatt aus „Heute"; das Notizbuch zeigt sie dort an.
 * - **Aufgabe** – `felder.aufgaben` trägt die Kennung der Work-Aufgabe (mehrere mit `|`, wie
 *   `bezuege` und `ausgeblendet`). Die Felder einer Art zeigt das Blatt nur für ihre eigenen Namen;
 *   dieses Feld bleibt also unsichtbar, bis „Notizen zur Aufgabe" es liest.
 *
 * Die Mappe wählt hier niemand: die Notiz kommt als Zettel mit dem Tag `schnelle-notiz` – die
 * Einordnung schlägt höchstens vor, abgelegt wird sie von Hand.
 *
 * **Ohne Netz** liegt die Notiz in der Warteschlange dieses Geräts und geht raus, sobald das
 * Notizbuch das nächste Mal offen ist.
 */

export interface NotizAblegenEingabe {
  text: string
  termin?: TerminKurz | null
  aufgabeId?: string | null
  tags?: readonly string[]
  /** `notiz`, `besprechung` … – ohne Angabe eine Notiz, mit Termin eine Besprechung. */
  art?: string
}

export type AblageErgebnis = 'gespeichert' | 'wartet' | 'nicht'

export function useNotizAblage(bevorzugteWelt?: string | null) {
  const { data: stand } = useStand()
  const freigegeben = !!stand?.freigegeben
  const { data: meine = [] } = useMeine(freigegeben)
  const speichern = useSpeichern()

  /**
   * **Ins Notizbuch der eigenen Welt, sonst ins erste beschreibbare.** Das Notizbuch selbst nimmt das
   * größte beschreibbare; dafür müsste es alle Listen laden. Für ein schnelles Feld reicht die Welt:
   * Arbeitszettel gehören dorthin, wo die Schnelle Aufgabe auch landet.
   */
  const ziel = useMemo(() => {
    const beschreibbar = meine.filter((n) => n.recht === 'schreiben' || n.recht === 'verwalten')
    return (
      beschreibbar.find((n) => bevorzugteWelt && n.workspaceId === bevorzugteWelt) ??
      beschreibbar[0] ??
      null
    )
  }, [meine, bevorzugteWelt])

  const ablegen = useCallback(
    (e: NotizAblegenEingabe): AblageErgebnis => {
      const roh = e.text.trim()
      if (!roh || !ziel) return 'nicht'
      const termine = e.termin
        ? [
            {
              titel: e.termin.titel,
              beginn: e.termin.beginn,
              ende: e.termin.ende,
              ...(e.termin.teilnehmer?.length ? { teilnehmer: e.termin.teilnehmer } : {}),
              ...(e.termin.serie ? { serie: e.termin.serie } : {}),
            },
          ]
        : undefined
      const eingabe: SpeichernEingabe = {
        notizbuchId: ziel.id,
        id: neueKennung(),
        titel: titelAusText(maskieren(roh)) || 'Schnelle Notiz',
        inhalt: maskieren(roh),
        art: e.art ?? (e.termin ? 'besprechung' : 'notiz'),
        mappe: null,
        datum: new Date().toISOString(),
        /**
         * `schnelle-notiz` (Nils, 23.10.2026, mit dev-b1 abgestimmt): solche Notizen legt Work nie von
         * selbst ab; sie bleiben im Zettelstapel und stehen in der Ansicht „Schnelle Notizen".
         */
        tags: [...new Set([...(e.tags ?? []), 'schnelle-notiz'])],
        ...(e.aufgabeId ? { felder: { aufgaben: e.aufgabeId } } : {}),
        ...(termine ? { termine } : {}),
      }
      if (typeof navigator !== 'undefined' && navigator.onLine === false) {
        return einreihen(eingabe) ? 'wartet' : 'nicht'
      }
      speichern.mutate(eingabe, { onError: () => void einreihen(eingabe) })
      return 'gespeichert'
    },
    [ziel, speichern],
  )

  return { freigegeben, ziel: ziel ? { id: ziel.id, name: ziel.name } : null, ablegen }
}
